/**
 * Zustand — this app's **client state**.
 *
 * Only what is true for this browser tab lives here: the transient toast queue.
 * Filters, the open item and the open panel are in the URL instead, so a link
 * reproduces the view; anything the server knows is in TanStack Query. Nothing
 * here copies either of those.
 *
 * Components select the narrowest slice they need (`useUiStore((s) => s.notify)`)
 * rather than the whole store, which would re-render them on every change.
 */

import { create } from "zustand";

export interface Toast {
  id: number;
  message: string;
  tone: "success" | "error";
  /** One button in the toast, e.g. Undo; clicking it also dismisses the toast. */
  action?: { label: string; run: () => void } | undefined;
}

interface UiState {
  toasts: Toast[];
  notify: (message: string, tone?: Toast["tone"], action?: Toast["action"]) => void;
  dismissToast: (id: number) => void;
}

let nextToastId = 0;

export const useUiStore = create<UiState>((set) => ({
  toasts: [],
  notify: (message, tone = "success", action) => {
    const id = nextToastId++;
    set((state) => ({ toasts: [...state.toasts, { id, message, tone, action }] }));
    // Auto-dismiss. Kept in the store rather than in a component so a toast
    // raised during navigation still disappears after the component that
    // triggered it has unmounted. One with an action (Undo) stays longer, so
    // there is time to reach it.
    setTimeout(
      () => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
      },
      action ? 8000 : 4000,
    );
  },
  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));
